import React, { useCallback, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { HqSsoCallback, LoginPage, SingpassCallbackPage } from './features/auth';
import SessionKeepAlive from './features/auth/SessionKeepAlive';
import QueuePage from './features/queue/QueuePage';
import DispensePage from './features/dispense/DispensePage';
import SignaturePage from './pages/SignaturePage';
import { ExternalConsultPage } from './features/external-consult';
import { StaffTeleconsultQueuePage } from './features/staff-teleconsult';
import { PatientReadyMeetToastStack } from './components/queue/PatientReadyMeetToastStack';
import { MeetSessionProvider, VideoConsultDock } from './features/video-consult';
import { useDoctorPatientReadyMeetHub } from './hooks/useDoctorPatientReadyMeetHub';
import { APP_BASENAME } from './appPaths';
import { Doctor, Patient, PortalMode } from './types/api';
import { mapStoredUserToDoctor, parseStoredUser } from './utils/doctorUser';
import {
  clearPersistedTeleSession,
  persistTelePortal,
  persistTeleSignatureGate,
  restoreTeleSessionFromStorage,
} from './utils/teleSession';

function readInitialAuthState(): {
  isLoggedIn: boolean;
  portalMode: PortalMode | null;
  doctor: Doctor | null;
  signatureULID: string | null;
} {
  const restored = restoreTeleSessionFromStorage();
  if (!restored) {
    return { isLoggedIn: false, portalMode: null, doctor: null, signatureULID: null };
  }
  return {
    isLoggedIn: true,
    portalMode: restored.portalMode,
    doctor: restored.doctor,
    signatureULID: restored.signatureULID,
  };
}

function App() {
  const initial = readInitialAuthState();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(initial.isLoggedIn);
  const [portalMode, setPortalMode] = useState<PortalMode | null>(initial.portalMode);
  const [doctor, setDoctor] = useState<Doctor | null>(initial.doctor);
  const [signatureULID, setSignatureULID] = useState<string | null>(initial.signatureULID);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const navigate = useNavigate();
  const doctorMeetAlerts = useDoctorPatientReadyMeetHub(
    isLoggedIn && portalMode === 'doctor' ? doctor?.id : null,
  );

  const handleLogin = (doctorInfo: Doctor, needsSignature: boolean, mode: PortalMode = 'doctor') => {
    setDoctor(doctorInfo);
    setPortalMode(mode);
    setIsLoggedIn(true);
    persistTelePortal(mode);
    if (mode === 'staff') {
      setSignatureULID('staff-skip');
      persistTeleSignatureGate('staff-skip');
      navigate('/staff-queue', { replace: true });
      return;
    }
    persistTeleSignatureGate(null);
    if (needsSignature) {
      navigate('/signature');
    } else {
      const gate = 'existing-sig-' + doctorInfo.id;
      setSignatureULID(gate);
      persistTeleSignatureGate(gate);
      navigate('/queue');
    }
  };

  const handleSignatureSave = () => {
    const u = parseStoredUser();
    if (u) {
      setDoctor((d) => d ?? mapStoredUserToDoctor(u));
      setIsLoggedIn(true);
    }
    setPortalMode('doctor');
    persistTelePortal('doctor');
    const gate = 'new-sig-' + Date.now();
    setSignatureULID(gate);
    persistTeleSignatureGate(gate);
    navigate('/queue');
  };

  const handleSelectPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    navigate('/dispense');
  };

  const handleBackToQueue = (notice?: string) => {
    setSelectedPatient(null);
    navigate(portalMode === 'staff' ? '/staff-queue' : '/queue', {
      state: notice ? { queueNotice: notice } : undefined,
    });
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    clearPersistedTeleSession();
    window.location.replace(`${APP_BASENAME}/login`);
  }, []);

  const loginRedirect = () => {
    if (!isLoggedIn) return <Navigate to="/login" replace />;
    if (portalMode === 'staff') return <Navigate to="/staff-queue" replace />;
    return <Navigate to="/queue" replace />;
  };

  return (
    <MeetSessionProvider enabled={isLoggedIn}>
      {isLoggedIn ? <SessionKeepAlive onLogout={handleLogout} /> : null}
      <Routes>
      <Route path="/singpass-callback" element={<SingpassCallbackPage />} />
      <Route path="/singpass-callback/:state" element={<SingpassCallbackPage />} />

      <Route path="/login" element={isLoggedIn ? loginRedirect() : <LoginPage onLogin={handleLogin} />} />
      <Route path="/sso/callback" element={<HqSsoCallback onLogin={handleLogin} />} />
      <Route
        path="/signature"
        element={
          !localStorage.getItem('accessToken') ? (
            <Navigate to="/login" replace />
          ) : portalMode === 'staff' ? (
            <Navigate to="/staff-queue" replace />
          ) : signatureULID ? (
            <Navigate to="/queue" replace />
          ) : (
            <SignaturePage doctor={doctor} onSave={handleSignatureSave} />
          )
        }
      />
      <Route
        path="/queue"
        element={
          !isLoggedIn || portalMode !== 'doctor' ? (
            <Navigate to="/login" replace />
          ) : !signatureULID ? (
            <Navigate to="/signature" replace />
          ) : (
            <QueuePage doctor={doctor} onSelectPatient={handleSelectPatient} onLogout={handleLogout} />
          )
        }
      />
      <Route
        path="/staff-queue"
        element={
          !isLoggedIn || portalMode !== 'staff' ? (
            <Navigate to="/login" replace />
          ) : (
            <StaffTeleconsultQueuePage staff={doctor} onSelectPatient={handleSelectPatient} onLogout={handleLogout} />
          )
        }
      />
      <Route
        path="/dispense"
        element={
          !isLoggedIn ? (
            <Navigate to="/login" replace />
          ) : !selectedPatient ? (
            <Navigate to={portalMode === 'staff' ? '/staff-queue' : '/queue'} replace />
          ) : (
            <DispensePage
              doctor={doctor}
              patient={selectedPatient}
              portalMode={portalMode ?? 'doctor'}
              onBack={handleBackToQueue}
              onLogout={handleLogout}
            />
          )
        }
      />

      <Route path="/consult/:token" element={<ExternalConsultPage />} />

      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      {isLoggedIn && portalMode === 'doctor' && doctor?.id ? (
        <PatientReadyMeetToastStack
          toasts={doctorMeetAlerts.toasts}
          onDismiss={doctorMeetAlerts.dismissToast}
        />
      ) : null}
      {isLoggedIn ? <VideoConsultDock /> : null}
    </MeetSessionProvider>
  );
}

export default App;
